import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { products, editorsPicks } from '@/mocks/products';
import PickOfTheWeek from './components/PickOfTheWeek';
import NewsletterSignup from './components/NewsletterSignup';
import SaleBanner from './components/SaleBanner';
import AffiliateDisclosure from '@/components/feature/AffiliateDisclosure';
import SEOHead from '@/components/feature/SEOHead';
import ShopifySetupBanner from '@/components/feature/ShopifySetupBanner';
import EditorPicksCarousel from './components/EditorPicksCarousel';
import BestSellersStrip from './components/BestSellersStrip';
import UnderTwentyDeals from './components/UnderTwentyDeals';
import NewArrivals from './components/NewArrivals';

const shopSchema = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: 'Cat Products Shop - MeowCentral',
  url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/shop`,
  description: 'Shop the best cat products hand-picked by cat lovers. Browse top-rated cat food, toys, grooming tools, furniture, and accessories from Amazon, Chewy, and PetSmart.',
  mainEntity: {
    '@type': 'ItemList',
    name: 'Best Cat Products',
    description: 'Curated list of top-rated cat products including food, toys, grooming tools, and accessories.',
    numberOfItems: products.length,
  },
};

export default function ShopPage() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('featured');
  const [showSortSheet, setShowSortSheet] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);

  const categories = [
    { id: 'all', name: 'All', icon: 'ri-store-2-line' },
    { id: 'Toys', name: 'Toys', icon: 'ri-gamepad-line' },
    { id: 'Food', name: 'Food', icon: 'ri-cake-3-line' },
    { id: 'Treats', name: 'Treats', icon: 'ri-heart-3-line' },
    { id: 'Furniture', name: 'Furniture', icon: 'ri-home-smile-line' },
    { id: 'Grooming', name: 'Grooming', icon: 'ri-scissors-cut-line' },
    { id: 'Health', name: 'Health', icon: 'ri-heart-pulse-line' },
    { id: 'Litter', name: 'Litter', icon: 'ri-recycle-line' },
    { id: 'Feeding', name: 'Feeding', icon: 'ri-bowl-line' },
    { id: 'Travel', name: 'Travel', icon: 'ri-flight-takeoff-line' },
    { id: 'Safety', name: 'Safety', icon: 'ri-shield-check-line' },
    { id: 'Bedding', name: 'Bedding', icon: 'ri-moon-line' },
    { id: 'Exercise', name: 'Exercise', icon: 'ri-run-line' },
  ];

  const filteredProducts = products
    .filter((product) => selectedCategory === 'all' || product.category === selectedCategory)
    .sort((a, b) => {
      if (sortBy === 'price-low') return a.price - b.price;
      if (sortBy === 'price-high') return b.price - a.price;
      if (sortBy === 'rating') return b.rating - a.rating;
      return 0;
    });

  const handleProductClick = (link: string) => {
    window.open(link, '_blank', 'noopener,noreferrer');
  };

  const getRetailerIcon = (retailer: string) => {
    switch (retailer) {
      case 'amazon':
        return 'ri-amazon-line';
      case 'chewy':
        return 'ri-shopping-bag-3-line';
      case 'petsmart':
        return 'ri-store-3-line';
      default:
        return 'ri-shopping-cart-line';
    }
  };

  const getRetailerName = (retailer: string) => {
    switch (retailer) {
      case 'amazon':
        return 'Amazon';
      case 'chewy':
        return 'Chewy';
      case 'petsmart':
        return 'PetSmart';
      default:
        return 'Shop Now';
    }
  };

  const getEditorCollection = (productIds: number[]) => {
    return products.filter((p) => productIds.includes(Number(p.id)));
  };

  const scrollToCategory = (categoryId: string) => {
    setSelectedCategory(categoryId);
    const element = document.getElementById('product-grid');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const sortOptions = [
    { value: 'featured', label: 'Featured' },
    { value: 'price-low', label: 'Price: Low to High' },
    { value: 'price-high', label: 'Price: High to Low' },
    { value: 'rating', label: 'Top Rated' },
  ];

  const isLimitedStock = (product: typeof products[0]) =>
    product.badge === 'SALE' || !!product.originalPrice;

  // Track scroll for back-to-top button
  const handleScroll = () => {
    setShowBackToTop(window.scrollY > 800);
  };

  // Add scroll listener
  useState(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 pb-20 md:pb-0">
      <SEOHead
        title="Best Cat Products Shop - Food, Toys & Accessories | MeowCentral"
        description="Shop hand-picked cat products tested by real felines. Top-rated cat food, toys, grooming tools, furniture, and accessories from Amazon, Chewy & PetSmart. Paw-approved picks updated weekly."
        keywords="cat products, cat toys, cat food, cat accessories, best cat products, cat grooming, cat furniture, buy cat supplies"
        canonical="/shop"
        schema={shopSchema}
      />
      <Navbar />
      
      {/* Shopify Setup Banner - Dev Mode Only */}
      {import.meta.env.DEV && <ShopifySetupBanner />}

      {/* Countdown Sale Banner */}
      <div className="pt-16">
        <SaleBanner />
      </div>

      {/* Affiliate Disclosure Banner */}
      <AffiliateDisclosure variant="banner" />

      {/* Premium Hero Banner with Animated Gradient */}
      <div className="px-4 pt-8 pb-6 transition-all duration-300">
        <div className="max-w-7xl mx-auto">
          <div className="relative bg-gradient-to-r from-teal-500 via-emerald-500 to-teal-600 rounded-2xl md:rounded-3xl overflow-hidden shadow-xl">
            {/* Animated gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-teal-400/30 via-transparent to-emerald-400/30 animate-pulse"></div>
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
            <div className="relative px-5 py-10 md:py-16 text-center">
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-xs font-bold mb-3">
                <i className="ri-award-line"></i>
                Paw-Approved 🐾
              </div>
              <h1 className="text-3xl md:text-5xl font-bold text-white mb-3 leading-tight">
                Best Cat Products
              </h1>
              <p className="text-sm md:text-lg text-white/90 max-w-2xl mx-auto mb-8 leading-relaxed">
                Hand-picked by cat lovers, tested by real felines
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
                <button
                  onClick={() => scrollToCategory('all')}
                  className="flex items-center justify-center gap-2 bg-white text-teal-600 font-bold py-3.5 px-6 rounded-full shadow-lg hover:shadow-xl transition-all min-h-[48px] cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-shopping-bag-3-line text-lg"></i>
                  Shop All Products
                </button>
                <Link
                  to="/blog"
                  className="flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm border-2 border-white text-white font-bold py-3.5 px-6 rounded-full hover:bg-white/20 transition-all min-h-[48px] cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-article-line text-lg"></i>
                  Read Guides
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pick of the Week */}
      <PickOfTheWeek />

      {/* Best Sellers Strip */}
      <BestSellersStrip onProductClick={handleProductClick} />

      {/* Under $20 Deals */}
      <UnderTwentyDeals onProductClick={handleProductClick} />

      {/* New Arrivals */}
      <NewArrivals onProductClick={handleProductClick} />

      {/* Newsletter Signup Section */}
      <NewsletterSignup />

      {/* Editor's Pick Collections */}
      <div className="px-4 py-8 md:py-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-800 mb-1">Editor's Picks</h2>
            <p className="text-sm md:text-base text-gray-600">Our top recommendations in each category</p>
          </div>

          {/* Best Cat Food Collection */}
          <div className="mb-10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg md:text-2xl font-bold text-gray-800 flex items-center gap-2">
                <i className="ri-cake-3-line text-teal-500 text-xl"></i>
                Best Food & Treats
              </h3>
              <button
                onClick={() => scrollToCategory('Food')}
                className="text-teal-600 hover:text-teal-700 font-semibold text-xs md:text-sm flex items-center gap-1 cursor-pointer whitespace-nowrap min-h-[44px] px-3"
              >
                View All <i className="ri-arrow-right-line"></i>
              </button>
            </div>
            <EditorPicksCarousel products={getEditorCollection(editorsPicks.bestFood)} onProductClick={handleProductClick} getRetailerIcon={getRetailerIcon} getRetailerName={getRetailerName} isLimitedStock={isLimitedStock} />
          </div>

          {/* Top Toys Collection */}
          <div className="mb-10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg md:text-2xl font-bold text-gray-800 flex items-center gap-2">
                <i className="ri-gamepad-line text-amber-500 text-xl"></i>
                Top Cat Toys
              </h3>
              <button
                onClick={() => scrollToCategory('Toys')}
                className="text-teal-600 hover:text-teal-700 font-semibold text-xs md:text-sm flex items-center gap-1 cursor-pointer whitespace-nowrap min-h-[44px] px-3"
              >
                View All <i className="ri-arrow-right-line"></i>
              </button>
            </div>
            <EditorPicksCarousel products={getEditorCollection(editorsPicks.topToys)} onProductClick={handleProductClick} getRetailerIcon={getRetailerIcon} getRetailerName={getRetailerName} isLimitedStock={isLimitedStock} />
          </div>

          {/* Grooming Essentials */}
          <div className="mb-10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg md:text-2xl font-bold text-gray-800 flex items-center gap-2">
                <i className="ri-scissors-cut-line text-rose-500 text-xl"></i>
                Grooming Tools
              </h3>
              <button
                onClick={() => scrollToCategory('Grooming')}
                className="text-teal-600 hover:text-teal-700 font-semibold text-xs md:text-sm flex items-center gap-1 cursor-pointer whitespace-nowrap min-h-[44px] px-3"
              >
                View All <i className="ri-arrow-right-line"></i>
              </button>
            </div>
            <EditorPicksCarousel products={getEditorCollection(editorsPicks.groomingEssentials)} onProductClick={handleProductClick} getRetailerIcon={getRetailerIcon} getRetailerName={getRetailerName} isLimitedStock={isLimitedStock} />
          </div>

          {/* Health & Wellness */}
          <div className="mb-10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg md:text-2xl font-bold text-gray-800 flex items-center gap-2">
                <i className="ri-heart-pulse-line text-emerald-500 text-xl"></i>
                Health & Wellness
              </h3>
              <button
                onClick={() => scrollToCategory('Health')}
                className="text-teal-600 hover:text-teal-700 font-semibold text-xs md:text-sm flex items-center gap-1 cursor-pointer whitespace-nowrap min-h-[44px] px-3"
              >
                View All <i className="ri-arrow-right-line"></i>
              </button>
            </div>
            <EditorPicksCarousel products={getEditorCollection(editorsPicks.healthWellness)} onProductClick={handleProductClick} getRetailerIcon={getRetailerIcon} getRetailerName={getRetailerName} isLimitedStock={isLimitedStock} />
          </div>

          {/* Furniture & Comfort */}
          <div className="mb-10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg md:text-2xl font-bold text-gray-800 flex items-center gap-2">
                <i className="ri-home-smile-line text-orange-500 text-xl"></i>
                Furniture & Comfort
              </h3>
              <button
                onClick={() => scrollToCategory('Furniture')}
                className="text-teal-600 hover:text-teal-700 font-semibold text-xs md:text-sm flex items-center gap-1 cursor-pointer whitespace-nowrap min-h-[44px] px-3"
              >
                View All <i className="ri-arrow-right-line"></i>
              </button>
            </div>
            <EditorPicksCarousel products={getEditorCollection(editorsPicks.furnitureComfort)} onProductClick={handleProductClick} getRetailerIcon={getRetailerIcon} getRetailerName={getRetailerName} isLimitedStock={isLimitedStock} />
          </div>

          {/* Travel & Safety */}
          <div className="mb-10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg md:text-2xl font-bold text-gray-800 flex items-center gap-2">
                <i className="ri-flight-takeoff-line text-sky-500 text-xl"></i>
                Travel & Safety
              </h3>
              <button
                onClick={() => scrollToCategory('Travel')}
                className="text-teal-600 hover:text-teal-700 font-semibold text-xs md:text-sm flex items-center gap-1 cursor-pointer whitespace-nowrap min-h-[44px] px-3"
              >
                View All <i className="ri-arrow-right-line"></i>
              </button>
            </div>
            <EditorPicksCarousel products={getEditorCollection(editorsPicks.travelSafety)} onProductClick={handleProductClick} getRetailerIcon={getRetailerIcon} getRetailerName={getRetailerName} isLimitedStock={isLimitedStock} />
          </div>

          {/* Feeding Supplies */}
          <div className="mb-10">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg md:text-2xl font-bold text-gray-800 flex items-center gap-2">
                <i className="ri-bowl-line text-violet-500 text-xl"></i>
                Feeding Supplies
              </h3>
              <button
                onClick={() => scrollToCategory('Feeding')}
                className="text-teal-600 hover:text-teal-700 font-semibold text-xs md:text-sm flex items-center gap-1 cursor-pointer whitespace-nowrap min-h-[44px] px-3"
              >
                View All <i className="ri-arrow-right-line"></i>
              </button>
            </div>
            <EditorPicksCarousel products={getEditorCollection(editorsPicks.feedingSupplies)} onProductClick={handleProductClick} getRetailerIcon={getRetailerIcon} getRetailerName={getRetailerName} isLimitedStock={isLimitedStock} />
          </div>

          {/* Litter Solutions */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg md:text-2xl font-bold text-gray-800 flex items-center gap-2">
                <i className="ri-recycle-line text-lime-600 text-xl"></i>
                Litter Solutions
              </h3>
              <button
                onClick={() => scrollToCategory('Litter')}
                className="text-teal-600 hover:text-teal-700 font-semibold text-xs md:text-sm flex items-center gap-1 cursor-pointer whitespace-nowrap min-h-[44px] px-3"
              >
                View All <i className="ri-arrow-right-line"></i>
              </button>
            </div>
            <EditorPicksCarousel products={getEditorCollection(editorsPicks.litterSolutions)} onProductClick={handleProductClick} getRetailerIcon={getRetailerIcon} getRetailerName={getRetailerName} isLimitedStock={isLimitedStock} />
          </div>
        </div>
      </div>

      {/* Category Filter - Horizontal Scroll on Mobile */}
      <div className="sticky top-16 z-30 bg-gradient-to-br from-amber-50 via-orange-50 to-rose-50 px-4 py-3 shadow-md">
        <div className="max-w-7xl mx-auto">
          <div className="flex gap-2 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-hide -mx-4 px-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex flex-col items-center justify-center gap-1 px-4 py-2.5 rounded-2xl whitespace-nowrap transition-all cursor-pointer snap-start flex-shrink-0 min-h-[64px] min-w-[72px] ${
                  selectedCategory === category.id
                    ? 'bg-teal-500 text-white shadow-lg shadow-teal-500/30 scale-105'
                    : 'bg-white text-gray-700 active:bg-teal-50 shadow-md'
                }`}
              >
                <i className={`${category.icon} text-2xl`}></i>
                <span className="font-medium text-xs">{category.name}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Sort & Count Bar */}
      <div id="product-grid" className="px-4 py-4 md:py-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <p className="text-sm md:text-base text-gray-600">
            <span className="font-bold text-teal-600">{filteredProducts.length}</span> products
            {selectedCategory !== 'all' && (
              <span className="ml-1 text-gray-500">in <span className="font-semibold text-gray-700">{selectedCategory}</span></span>
            )}
          </p>
          {/* Desktop Sort Dropdown */}
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="hidden md:block px-4 py-2 rounded-full border-2 border-teal-200 focus:border-teal-400 focus:outline-none bg-white cursor-pointer text-sm"
          >
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {/* Mobile Sort Button */}
          <button
            onClick={() => setShowSortSheet(true)}
            className="md:hidden flex items-center gap-2 px-4 py-2 rounded-full border-2 border-teal-200 bg-white text-gray-700 font-medium text-sm cursor-pointer min-h-[40px]"
          >
            <i className="ri-sort-desc"></i>
            Sort
          </button>
        </div>
      </div>

      {/* Product Grid */}
      <div className="px-4 pb-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6" data-product-shop>
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                className="bg-white rounded-2xl overflow-hidden shadow-lg active:shadow-2xl transition-all duration-300 active:scale-[0.98] cursor-pointer group relative"
              >
                {/* Out of Stock Overlay */}
                {!product.inStock && (
                  <div className="absolute inset-0 bg-gray-900/75 backdrop-blur-sm z-10 flex flex-col items-center justify-center rounded-2xl">
                    <i className="ri-close-circle-line text-5xl text-white mb-2"></i>
                    <p className="text-white font-bold text-lg">Currently Out of Stock</p>
                    <p className="text-white/80 text-sm mt-1">Check back soon</p>
                  </div>
                )}
                <div
                  onClick={() => product.inStock && handleProductClick(product.link)}
                  className="relative overflow-hidden"
                >
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-56 md:h-64 object-cover object-top"
                  />
                  {product.badge && (
                    <div className={`absolute top-3 left-3 px-3 py-1 rounded-full text-xs font-bold ${
                      product.badge === 'SALE' ? 'bg-rose-500 text-white' :
                      product.badge === 'BESTSELLER' ? 'bg-amber-500 text-white' :
                      product.badge === 'TOP RATED' ? 'bg-teal-500 text-white' :
                      'bg-gray-700 text-white'
                    }`}>
                      {product.badge}
                    </div>
                  )}
                  {isLimitedStock(product) && product.inStock && (
                    <div className="absolute bottom-3 left-3 flex items-center gap-1.5 bg-rose-600 text-white px-2.5 py-1 rounded-full text-xs font-bold shadow-lg">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
                      </span>
                      Limited Stock
                    </div>
                  )}
                </div>
                <div className="p-4">
                  {/* Honest badge replacing fake stars */}
                  <div className="flex items-center gap-1 mb-2">
                    <i className="ri-medal-line text-amber-500 text-sm"></i>
                    <span className="text-xs font-semibold text-amber-600">Highly Rated on Chewy</span>
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-2 line-clamp-2 text-sm leading-snug min-h-[40px]">
                    {product.name}
                  </h3>
                  <p className="text-xs text-gray-500 mb-2 px-2 py-1 bg-gray-50 rounded-lg italic line-clamp-2">
                    "{product.whyWeLoveIt}"
                  </p>
                  <div className="flex items-center gap-1 mb-3">
                    <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">{product.category}</span>
                  </div>
                  <div className="flex items-baseline gap-2 mb-3">
                    <span className="text-xl font-bold text-teal-600">${product.price}</span>
                    {product.originalPrice && (
                      <span className="text-sm text-gray-400 line-through">
                        ${product.originalPrice}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => product.inStock && handleProductClick(product.link)}
                    disabled={!product.inStock}
                    className={`w-full font-bold py-3 px-4 rounded-full flex items-center justify-center gap-2 transition-colors mb-2 whitespace-nowrap min-h-[48px] ${
                      product.inStock
                        ? 'bg-teal-500 active:bg-teal-600 text-white cursor-pointer'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <i className={getRetailerIcon(product.retailer)}></i>
                    {product.inStock ? `Shop on ${getRetailerName(product.retailer)}` : 'Out of Stock'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-24 md:bottom-8 right-4 md:right-8 w-12 h-12 bg-teal-500 hover:bg-teal-600 text-white rounded-full shadow-2xl flex items-center justify-center z-40 transition-all hover:scale-110 active:scale-95 cursor-pointer"
          aria-label="Back to top"
        >
          <i className="ri-arrow-up-line text-2xl"></i>
        </button>
      )}

      {/* Mobile Bottom Sort Sheet */}
      {showSortSheet && (
        <div
          className="fixed inset-0 bg-black/50 z-50 md:hidden"
          onClick={() => setShowSortSheet(false)}
        >
          <div
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl shadow-2xl animate-slide-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-800">Sort Products</h3>
                <button
                  onClick={() => setShowSortSheet(false)}
                  className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-full cursor-pointer"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
              <div className="space-y-2">
                {sortOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      setSortBy(option.value);
                      setShowSortSheet(false);
                    }}
                    className={`w-full text-left px-4 py-3.5 rounded-xl transition-colors cursor-pointer min-h-[52px] flex items-center justify-between ${
                      sortBy === option.value
                        ? 'bg-teal-50 text-teal-600 font-bold'
                        : 'bg-gray-50 text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <span>{option.label}</span>
                    {sortBy === option.value && (
                      <i className="ri-check-line text-xl"></i>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sticky Bottom Navigation - Mobile Only */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-gray-200 shadow-2xl z-40 md:hidden">
        <div className="flex items-center justify-around py-2">
          <button
            onClick={() => scrollToCategory('Toys')}
            className={`flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-xl transition-colors cursor-pointer min-w-[64px] ${
              selectedCategory === 'Toys' ? 'text-teal-600' : 'text-gray-600'
            }`}
          >
            <i className="ri-gamepad-line text-2xl"></i>
            <span className="text-xs font-medium">Toys</span>
          </button>
          <button
            onClick={() => scrollToCategory('Food')}
            className={`flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-xl transition-colors cursor-pointer min-w-[64px] ${
              selectedCategory === 'Food' ? 'text-teal-600' : 'text-gray-600'
            }`}
          >
            <i className="ri-cake-3-line text-2xl"></i>
            <span className="text-xs font-medium">Food</span>
          </button>
          <button
            onClick={() => scrollToCategory('Grooming')}
            className={`flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-xl transition-colors cursor-pointer min-w-[64px] ${
              selectedCategory === 'Grooming' ? 'text-teal-600' : 'text-gray-600'
            }`}
          >
            <i className="ri-scissors-cut-line text-2xl"></i>
            <span className="text-xs font-medium">Groom</span>
          </button>
          <button
            onClick={() => scrollToCategory('Furniture')}
            className={`flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-xl transition-colors cursor-pointer min-w-[64px] ${
              selectedCategory === 'Furniture' ? 'text-teal-600' : 'text-gray-600'
            }`}
          >
            <i className="ri-home-smile-line text-2xl"></i>
            <span className="text-xs font-medium">Home</span>
          </button>
          <button
            onClick={() => scrollToCategory('all')}
            className={`flex flex-col items-center justify-center gap-1 px-3 py-2 rounded-xl transition-colors cursor-pointer min-w-[64px] ${
              selectedCategory === 'all' ? 'text-teal-600' : 'text-gray-600'
            }`}
          >
            <i className="ri-store-2-line text-2xl"></i>
            <span className="text-xs font-medium">All</span>
          </button>
        </div>
      </div>

      {/* Trust Banner */}
      <div className="bg-white py-10 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mb-3">
                <i className="ri-shield-check-line text-2xl text-teal-600"></i>
              </div>
              <h4 className="font-semibold text-gray-800 text-sm">Trusted Retailers</h4>
              <p className="text-xs text-gray-500 mt-1">Chewy, Amazon, PetSmart</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mb-3">
                <i className="ri-star-line text-2xl text-amber-600"></i>
              </div>
              <h4 className="font-semibold text-gray-800 text-sm">Top Rated</h4>
              <p className="text-xs text-gray-500 mt-1">4+ star products only</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-rose-100 rounded-full flex items-center justify-center mb-3">
                <i className="ri-truck-line text-2xl text-rose-600"></i>
              </div>
              <h4 className="font-semibold text-gray-800 text-sm">Fast Shipping</h4>
              <p className="text-xs text-gray-500 mt-1">Direct from retailers</p>
            </div>
            <div className="flex flex-col items-center">
              <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-3">
                <i className="ri-refund-2-line text-2xl text-emerald-600"></i>
              </div>
              <h4 className="font-semibold text-gray-800 text-sm">Easy Returns</h4>
              <p className="text-xs text-gray-500 mt-1">Retailer policies apply</p>
            </div>
          </div>
        </div>
      </div>

      {/* Brand Partnership Section */}
      <div className="relative py-16 md:py-20 px-4 overflow-hidden">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50 via-amber-50 to-rose-50"></div>
        
        {/* Decorative Elements */}
        <div className="absolute top-10 left-10 text-6xl opacity-10">🐱</div>
        <div className="absolute bottom-10 right-10 text-6xl opacity-10">🐾</div>
        
        <div className="relative max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10 md:mb-12">
            <div className="inline-flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-bold text-teal-600 mb-4 shadow-md">
              <i className="ri-handshake-line text-lg"></i>
              For Brands & Partners
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Partner With Us 🐱
            </h2>
            <p className="text-base md:text-lg text-gray-700 max-w-2xl mx-auto mb-8">
              Get your cat products in front of thousands of passionate cat lovers who trust our recommendations
            </p>
            
            {/* Stats */}
            <div className="flex flex-wrap justify-center gap-6 md:gap-8">
              <div className="flex flex-col items-center">
                <div className="text-3xl md:text-4xl font-black text-teal-600 mb-1">{products.length}+</div>
                <div className="text-sm md:text-base text-gray-600 font-medium">Products Featured</div>
              </div>
              <div className="flex flex-col items-center">
                <div className="text-3xl md:text-4xl font-black text-amber-600 mb-1">10K+</div>
                <div className="text-sm md:text-base text-gray-600 font-medium">Monthly Visitors</div>
              </div>
              <div className="flex flex-col items-center">
                <div className="text-3xl md:text-4xl font-black text-rose-600 mb-1">Weekly</div>
                <div className="text-sm md:text-base text-gray-600 font-medium">Curated Picks</div>
              </div>
            </div>
          </div>

          {/* Partnership Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-4xl mx-auto">
            {/* Feature Your Product Card */}
            <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-3xl p-8 md:p-10 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-[1.02] group">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-trophy-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                Feature Your Product
              </h3>
              <p className="text-white/90 text-base md:text-lg mb-6 leading-relaxed">
                Get your cat products featured in our Pick of the Week, Editor's Picks, or dedicated product reviews. We showcase the best to our engaged community.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3 text-white/90">
                  <i className="ri-check-line text-2xl flex-shrink-0"></i>
                  <span className="text-sm md:text-base">Featured in weekly newsletter</span>
                </li>
                <li className="flex items-start gap-3 text-white/90">
                  <i className="ri-check-line text-2xl flex-shrink-0"></i>
                  <span className="text-sm md:text-base">Dedicated blog post review</span>
                </li>
                <li className="flex items-start gap-3 text-white/90">
                  <i className="ri-check-line text-2xl flex-shrink-0"></i>
                  <span className="text-sm md:text-base">Social media promotion</span>
                </li>
              </ul>
              <Link
                to="/contact"
                className="flex items-center justify-center gap-2 w-full bg-white text-teal-600 font-bold text-base md:text-lg py-4 px-6 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105 active:scale-95 whitespace-nowrap min-h-[56px] cursor-pointer"
              >
                Get in Touch
                <i className="ri-arrow-right-line text-xl"></i>
              </Link>
            </div>

            {/* Affiliate Partner Card */}
            <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-3xl p-8 md:p-10 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-[1.02] group">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <i className="ri-links-line text-4xl text-white"></i>
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                Become an Affiliate Partner
              </h3>
              <p className="text-white/90 text-base md:text-lg mb-6 leading-relaxed">
                Already have an affiliate program? We'd love to promote your products to our cat-loving audience and earn together through performance-based partnerships.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-start gap-3 text-white/90">
                  <i className="ri-check-line text-2xl flex-shrink-0"></i>
                  <span className="text-sm md:text-base">Performance-based commissions</span>
                </li>
                <li className="flex items-start gap-3 text-white/90">
                  <i className="ri-check-line text-2xl flex-shrink-0"></i>
                  <span className="text-sm md:text-base">Authentic product recommendations</span>
                </li>
                <li className="flex items-start gap-3 text-white/90">
                  <i className="ri-check-line text-2xl flex-shrink-0"></i>
                  <span className="text-sm md:text-base">Long-term partnership opportunities</span>
                </li>
              </ul>
              <Link
                to="/contact"
                className="flex items-center justify-center gap-2 w-full bg-white text-amber-600 font-bold text-base md:text-lg py-4 px-6 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105 active:scale-95 whitespace-nowrap min-h-[56px] cursor-pointer"
              >
                Partner With Us
                <i className="ri-arrow-right-line text-xl"></i>
              </Link>
            </div>
          </div>

          {/* Bottom Note */}
          <div className="mt-10 text-center">
            <p className="text-sm md:text-base text-gray-600 max-w-2xl mx-auto">
              We only feature products we genuinely believe in. Our audience trusts our recommendations, and we take that responsibility seriously. 🐾
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}